// ===========================================
// BUSTED! BLACKJACK - PIXI.JS HOW TO BET MODAL
// ===========================================
// Pure PixiJS implementation of the "How to Bet" tutorial modal.
// Uses MODAL_FRAME token for consistent styling with DOM modals.
// ===========================================

import { Container, Graphics, Text, TextStyle, Rectangle } from 'pixi.js';
import { MODAL_FRAME } from '@busted-blackjack/shared';

// ===========================================
// TYPES
// ===========================================

export interface PixiHowToBetModalConfig {
  /** Callback when OK is clicked */
  onClose: (dontShowAgain: boolean) => void;
  /** Canvas dimensions for centering */
  canvasWidth: number;
  canvasHeight: number;
}

// ===========================================
// CONSTANTS (derived from MODAL_FRAME token)
// ===========================================
// Convert hex string to number: '#eab308' -> 0xeab308
const hexToNumber = (hex: string): number => parseInt(hex.replace('#', ''), 16);

const PANEL_WIDTH = 340;
const PANEL_HEIGHT = 220;
const PANEL_RADIUS = MODAL_FRAME.radiusPx;
const PANEL_BG_COLOR = hexToNumber(MODAL_FRAME.bodyBg);
const PANEL_BG_ALPHA = 0.95;
const PANEL_BORDER_COLOR = hexToNumber(MODAL_FRAME.borderColor.default);
const PANEL_BORDER_WIDTH = MODAL_FRAME.borderWidthPx;

const BUTTON_WIDTH = 120;
const BUTTON_HEIGHT = 44;
const BUTTON_RADIUS = 8;

const OK_COLOR = 0xffd700; // Gold
const OK_HOVER_COLOR = 0xffec8b; // Lighter gold
const OK_TEXT_COLOR = 0x1a1a2e; // Dark text on gold

const CHECKBOX_SIZE = 18;
const CHECKBOX_BORDER_COLOR = 0x666666;
const CHECKBOX_CHECKED_COLOR = 0xffd700;

// ===========================================
// PIXI HOW TO BET MODAL CLASS
// ===========================================

export class PixiHowToBetModal extends Container {
  private config: PixiHowToBetModalConfig;
  private background: Graphics;
  private titleText: Text;
  private instruction1: Text;
  private instruction2: Text;
  private okButton: Graphics;
  private okButtonText: Text;
  private checkbox: Graphics;
  private checkboxLabel: Text;

  // State
  private dontShowAgain: boolean = false;
  private okHovered: boolean = false;

  constructor(config: PixiHowToBetModalConfig) {
    super();
    this.config = config;
    this.eventMode = 'static';
    this.visible = false;

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // Create title
    this.titleText = new Text({
      text: 'HOW TO BET',
      style: new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: 26,
        fontWeight: 'bold',
        fill: 0xffffff,
      }),
    });
    this.titleText.anchor.set(0.5, 0);
    this.addChild(this.titleText);

    // Create instructions (with word wrap to fit panel width)
    const instructionWrapWidth = PANEL_WIDTH - 50; // 25px padding on each side

    this.instruction1 = new Text({
      text: '1. Tap your chips on the rail to size your bet.',
      style: new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: 15,
        fill: 0xcccccc,
        wordWrap: true,
        wordWrapWidth: instructionWrapWidth,
      }),
    });
    this.instruction1.anchor.set(0, 0);
    this.addChild(this.instruction1);

    this.instruction2 = new Text({
      text: '2. Tap BET on the Control Panel to place your bet.',
      style: new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: 15,
        fill: 0xcccccc,
        wordWrap: true,
        wordWrapWidth: instructionWrapWidth,
      }),
    });
    this.instruction2.anchor.set(0, 0);
    this.addChild(this.instruction2);

    // Create OK button
    this.okButton = new Graphics();
    this.okButton.eventMode = 'static';
    this.okButton.cursor = 'pointer';
    this.okButton.hitArea = new Rectangle(0, 0, BUTTON_WIDTH, BUTTON_HEIGHT);
    this.addChild(this.okButton);

    this.okButtonText = new Text({
      text: 'OK',
      style: new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: 18,
        fontWeight: 'bold',
        fill: OK_TEXT_COLOR,
      }),
    });
    this.okButtonText.anchor.set(0.5, 0.5);
    this.okButtonText.eventMode = 'none';
    this.addChild(this.okButtonText);

    // OK button events
    this.okButton.on('pointerover', () => {
      this.okHovered = true;
      this.drawOkButton();
    });
    this.okButton.on('pointerout', () => {
      this.okHovered = false;
      this.drawOkButton();
    });
    this.okButton.on('pointerdown', () => {
      this.hide();
      this.config.onClose(this.dontShowAgain);
    });

    // Create checkbox
    this.checkbox = new Graphics();
    this.checkbox.eventMode = 'static';
    this.checkbox.cursor = 'pointer';
    this.checkbox.hitArea = new Rectangle(0, 0, CHECKBOX_SIZE, CHECKBOX_SIZE);
    this.addChild(this.checkbox);

    this.checkboxLabel = new Text({
      text: "Don't show again",
      style: new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: 12,
        fill: 0x888888,
      }),
    });
    this.checkboxLabel.anchor.set(0, 0.5);
    this.checkboxLabel.eventMode = 'static';
    this.checkboxLabel.cursor = 'pointer';
    this.addChild(this.checkboxLabel);

    // Checkbox events
    this.checkbox.on('pointerdown', () => this.toggleCheckbox());
    this.checkboxLabel.on('pointerdown', () => this.toggleCheckbox());

    // Build layout
    this.buildLayout();
  }

  private toggleCheckbox(): void {
    this.dontShowAgain = !this.dontShowAgain;
    this.drawCheckbox();
  }

  private drawOkButton(): void {
    this.okButton.clear();
    const color = this.okHovered ? OK_HOVER_COLOR : OK_COLOR;
    this.okButton.roundRect(0, 0, BUTTON_WIDTH, BUTTON_HEIGHT, BUTTON_RADIUS);
    this.okButton.fill({ color, alpha: 1 });
  }

  private drawCheckbox(): void {
    this.checkbox.clear();

    // Border
    this.checkbox.roundRect(0, 0, CHECKBOX_SIZE, CHECKBOX_SIZE, 3);
    this.checkbox.stroke({ color: CHECKBOX_BORDER_COLOR, width: 2 });

    if (this.dontShowAgain) {
      // Fill when checked
      this.checkbox.roundRect(2, 2, CHECKBOX_SIZE - 4, CHECKBOX_SIZE - 4, 2);
      this.checkbox.fill({ color: CHECKBOX_CHECKED_COLOR });

      // Checkmark
      this.checkbox.moveTo(4, CHECKBOX_SIZE / 2);
      this.checkbox.lineTo(CHECKBOX_SIZE / 2 - 1, CHECKBOX_SIZE - 5);
      this.checkbox.lineTo(CHECKBOX_SIZE - 4, 4);
      this.checkbox.stroke({ color: PANEL_BG_COLOR, width: 2 });
    }
  }

  private buildLayout(): void {
    // Draw background
    this.background.clear();
    this.background.roundRect(0, 0, PANEL_WIDTH, PANEL_HEIGHT, PANEL_RADIUS);
    this.background.fill({ color: PANEL_BG_COLOR, alpha: PANEL_BG_ALPHA });
    this.background.stroke({ color: PANEL_BORDER_COLOR, width: PANEL_BORDER_WIDTH });

    // Position title
    this.titleText.x = PANEL_WIDTH / 2;
    this.titleText.y = 22;

    // Position instructions
    const instructionX = 25;
    this.instruction1.x = instructionX;
    this.instruction1.y = 58;
    this.instruction2.x = instructionX;
    this.instruction2.y = 98;

    // Position OK button (centered)
    const buttonX = (PANEL_WIDTH - BUTTON_WIDTH) / 2;
    const buttonY = 130;
    this.okButton.x = buttonX;
    this.okButton.y = buttonY;
    this.okButtonText.x = buttonX + BUTTON_WIDTH / 2;
    this.okButtonText.y = buttonY + BUTTON_HEIGHT / 2;
    this.drawOkButton();

    // Position checkbox (centered below button)
    const checkboxAreaWidth = CHECKBOX_SIZE + 8 + this.checkboxLabel.width;
    const checkboxX = (PANEL_WIDTH - checkboxAreaWidth) / 2;
    const checkboxY = 185;
    this.checkbox.x = checkboxX;
    this.checkbox.y = checkboxY;
    this.checkboxLabel.x = checkboxX + CHECKBOX_SIZE + 8;
    this.checkboxLabel.y = checkboxY + CHECKBOX_SIZE / 2;
    this.drawCheckbox();

    // Center panel on canvas
    this.updatePosition();
  }

  /**
   * Update position to center on canvas
   */
  public updatePosition(): void {
    this.x = (this.config.canvasWidth - PANEL_WIDTH) / 2;
    this.y = (this.config.canvasHeight - PANEL_HEIGHT) / 2;
  }

  /**
   * Update canvas dimensions (call when canvas resizes)
   */
  public updateConfig(config: Partial<PixiHowToBetModalConfig>): void {
    Object.assign(this.config, config);
    this.updatePosition();
  }

  /**
   * Show the modal
   */
  public show(): void {
    this.dontShowAgain = false;
    this.drawCheckbox();
    this.visible = true;
  }

  /**
   * Hide the modal
   */
  public hide(): void {
    this.visible = false;
  }

  /**
   * Check if modal is visible
   */
  public isShowing(): boolean {
    return this.visible;
  }

  /**
   * Cleanup - call when removing from scene
   */
  public dispose(): void {
    this.background.destroy();
    this.titleText.destroy();
    this.instruction1.destroy();
    this.instruction2.destroy();
    this.okButton.destroy();
    this.okButtonText.destroy();
    this.checkbox.destroy();
    this.checkboxLabel.destroy();
    this.destroy();
  }
}
